import { createEmptySelectedObjects } from '../selection/selection-transitions';
import {
  buildProductionCallbackPayload,
  buildSelectedPanelsStateFromCanvasSelection,
  getPanelCounts,
  getProductionPerPanelResult,
  getProductionPerStringResult,
  getSelectedPanelsPerRoofFromDesign,
  mapRoofKeyedPayloadToDesign,
  mapSelectedPanelsPerRoofToDesign,
  mapStringingDataToDesign,
  normalizeProductionPerPanelData
} from '../production/production-mapping';
import {
  hasFixedInverterQuantity,
  resolveAppliedPanelCount
} from '../inverter/inverter';
import { buildSyntheticStringingFromSelectedPanels } from './proposal-mapping';

export function createInitialReportDomainState() {
  return {
    designData: {},
    proposalVersion: null,
    isLegacyProposal: false,
    legacyRuntimeProductionSnapshot: null,
    pendingLegacyProductionHydration: false,
    loadError: null,
    panel: {},
    inverter: {},
    inverterQuantity: 1,
    additionalInverters: [],
    companyPanel: null,
    companyInverter: null,
    hasAutoCalculatedInverterQuantity: false,
    primaryInverterAutoQuantity: true,
    monitoring: {},
    mounting: {},
    adders: {},
    permitCost: 0,
    selectedPanels: null,
    totalPanels: 0,
    systemSize: 0,
    annualUsage: 0,
    solarProduction: 0,
    monthlyProduction: [],
    offset: 0,
    selectedObjects: createEmptySelectedObjects(),
    viewMode: '3D',
    shadingTexture: null,
    shadingMonth: -1,
    panelShadings: [],
    monthlyPanelShadings: [],
    shadingReportData: null,
    annotations: [],
    ambientTemp: null,
    productionIsCalculating: false,
    productionPerPanelData: [],
    productionPerStringData: [],
    stringingData: null,
    invertersUsed: null,
    clippingKwh: 0,
    monthlyClippingKwh: Array(12).fill(0),
    dcAcRatio: null,
    stringingPanelIndexMode: 'all',
    stringingSelectedPanelIndexMap: null,
    stringingRoofIndexMap: null,
    hydratingSelectionFromStringing: false,
    showStringing: false,
    showOverlay: false,
    scale: null,
    visibility: true
  };
}

export function buildEmptyReportProductionState() {
  const emptyMonthly = Array(12).fill(0);
  return {
    productionIsCalculating: false,
    solarProduction: 0,
    monthlyProduction: emptyMonthly,
    systemSize: 0,
    totalPanels: 0,
    productionPerPanelData: [],
    productionPerStringData: [],
    offset: 0,
    stringingData: null,
    invertersUsed: null,
    clippingKwh: 0,
    monthlyClippingKwh: emptyMonthly,
    dcAcRatio: null
  };
}

export function buildReportClippingPatch(result = {}) {
  return {
    clippingKwh: result.annual_clipping_kwh || 0,
    monthlyClippingKwh: result.monthly_clipping_kwh || Array(12).fill(0),
    dcAcRatio: result.dc_ac_ratio
  };
}

export function applyReportSolarProductionResult(
  state = {},
  result = {},
  ctx = {}
) {
  const annualUsage =
    ctx.annualUsage !== undefined ? ctx.annualUsage : state.annualUsage || 0;
  const apiAnnualKwh =
    ctx.apiAnnualKwh !== undefined ? ctx.apiAnnualKwh : result.annual_kwh || 0;
  const apiMonthlyKwh =
    ctx.apiMonthlyKwh !== undefined
      ? ctx.apiMonthlyKwh
      : result.monthly_kwh || Array(12).fill(0);
  const apiSystemSize = ctx.apiSystemSize !== undefined ? ctx.apiSystemSize : 0;
  const apiPanelCount = ctx.apiPanelCount !== undefined ? ctx.apiPanelCount : 0;
  const apiOffset =
    ctx.apiOffset !== undefined
      ? ctx.apiOffset
      : annualUsage > 0
        ? (apiAnnualKwh / annualUsage) * 100
        : 0;
  const productionPerPanelRaw =
    ctx.productionPerPanelData !== undefined
      ? ctx.productionPerPanelData
      : getProductionPerPanelResult(result);
  const productionPerStringRaw =
    ctx.productionPerStringData !== undefined
      ? ctx.productionPerStringData
      : getProductionPerStringResult(result);
  const strings =
    ctx.strings !== undefined
      ? ctx.strings
      : result.solar_design?.[0]?.strings || null;
  const invertersUsed =
    ctx.invertersUsed !== undefined
      ? ctx.invertersUsed
      : result.solar_design?.[0]?.inverters_used || null;

  const patch = {
    stringingData: strings,
    stringingPanelIndexMode: ctx.stringingPanelIndexMode ?? 'all',
    invertersUsed,
    ...buildReportClippingPatch(result),
    productionIsCalculating: false,
    solarProduction: apiAnnualKwh,
    monthlyProduction: apiMonthlyKwh,
    systemSize: apiSystemSize,
    totalPanels: apiPanelCount,
    productionPerPanelData: Array.isArray(productionPerPanelRaw)
      ? productionPerPanelRaw
      : [],
    productionPerStringData: Array.isArray(productionPerStringRaw)
      ? productionPerStringRaw
      : [],
    offset: Math.round(apiOffset)
  };

  if (ctx.selectedPanels !== undefined) {
    patch.selectedPanels = ctx.selectedPanels;
  }

  return patch;
}

export function mapReportSolarProductionResult({
  state = {},
  result = {},
  design = [],
  previousSelectedPanels = {},
  selectedPanelsPerRoofFallback = null,
  perRoofCounts = [],
  panelData = null,
  designPanels = null,
  roofIndexMap = null,
  panelIndexMap = null,
  requestedPanelCount = 0,
  panelPower = 0,
  annualUsage = 0,
  primaryInverterAutoQuantity = true,
  additionalInverters = [],
  synthesizeMissingStringing = false
} = {}) {
  const panelCounts = getPanelCounts({
    panelData,
    design,
    designPanels
  });
  const resolvedPerRoofCounts =
    Array.isArray(perRoofCounts) && perRoofCounts.length > 0
      ? perRoofCounts
      : panelCounts.perRoofCounts;
  const mappedStrings = mapStringingDataToDesign(
    result?.solar_design?.[0]?.strings || null,
    roofIndexMap,
    panelIndexMap
  );
  const hasResultSelection =
    result?.selected_panels_per_roof &&
    Object.keys(result.selected_panels_per_roof).length > 0;
  const fallbackSelection =
    selectedPanelsPerRoofFallback ??
    getSelectedPanelsPerRoofFromDesign(design, previousSelectedPanels);
  const selectedPanelsPerRoof = hasResultSelection
    ? mapSelectedPanelsPerRoofToDesign(
        result.selected_panels_per_roof,
        roofIndexMap,
        panelIndexMap
      )
    : fallbackSelection || {};
  const strings =
    synthesizeMissingStringing &&
    (!Array.isArray(mappedStrings) || mappedStrings.length === 0)
      ? buildSyntheticStringingFromSelectedPanels(selectedPanelsPerRoof)
      : mappedStrings;
  const selectedPanels = buildSelectedPanelsStateFromCanvasSelection(
    design,
    selectedPanelsPerRoof,
    previousSelectedPanels
  );
  const productionPerPanelData = normalizeProductionPerPanelData({
    raw: getProductionPerPanelResult(result),
    perRoofCounts: resolvedPerRoofCounts,
    roofIndexMap,
    panelIndexMap
  });
  const productionPerStringData = getProductionPerStringResult(result);
  const apiPanelCount = resolveAppliedPanelCount({
    resultPanelCount: result.panel_count,
    selectedPanelsPerRoof,
    fallbackPanelCount: requestedPanelCount
  });
  const apiSystemSize = apiPanelCount * (Number(panelPower || 0) / 1000);
  const apiAnnualKwh = result.annual_kwh || 0;
  const apiMonthlyKwh = result.monthly_kwh || Array(12).fill(0);
  const apiOffset =
    Number(annualUsage) > 0 ? (apiAnnualKwh / Number(annualUsage)) * 100 : 0;
  const limitReached =
    hasFixedInverterQuantity({
      primaryInverterAutoQuantity,
      additionalInverters
    }) && requestedPanelCount > apiPanelCount;
  const perRoofAnnualKwh = mapRoofKeyedPayloadToDesign(
    result.per_roof_annual_kwh,
    roofIndexMap
  );
  const perRoofMonthlyAc = mapRoofKeyedPayloadToDesign(
    result.per_roof_monthly_ac,
    roofIndexMap
  );
  const perRoofMonthlyPoa = mapRoofKeyedPayloadToDesign(
    result.per_roof_monthly_poa,
    roofIndexMap
  );
  const perRoofClippingKwh = mapRoofKeyedPayloadToDesign(
    result.per_roof_clipping_kwh,
    roofIndexMap
  );
  const reportStatePatchInputs = {
    annualUsage,
    apiAnnualKwh,
    apiMonthlyKwh,
    apiSystemSize,
    apiPanelCount,
    apiOffset,
    strings,
    productionPerPanelData,
    productionPerStringData,
    selectedPanels,
    stringingPanelIndexMode: 'all'
  };
  const reportStatePatch = applyReportSolarProductionResult(
    state,
    result,
    reportStatePatchInputs
  );

  return {
    strings,
    selectedPanels,
    selectedPanelsPerRoof,
    productionPerPanelData,
    productionPerStringData,
    perRoofAnnualKwh,
    perRoofMonthlyAc,
    perRoofMonthlyPoa,
    perRoofClippingKwh,
    numTotalPanels: panelCounts.totalCount,
    perRoofCounts: resolvedPerRoofCounts,
    apiPanelCount,
    apiSystemSize,
    apiAnnualKwh,
    apiMonthlyKwh,
    apiOffset,
    limitReached,
    invertersUsed: result?.solar_design?.[0]?.inverters_used || null,
    mpptCountTotal: result.mppt_count_total,
    reportStatePatchInputs,
    reportStatePatch
  };
}

export function buildReportInverterChangePatch(
  state = {},
  {
    inverter,
    inverterQuantity = 1,
    additionalInverters = [],
    persistedAdditionalInverters = [],
    primaryInverterAutoQuantity = true
  } = {}
) {
  const hasAutoCalculatedInverterQuantity = !primaryInverterAutoQuantity;
  return {
    inverter,
    inverterQuantity,
    additionalInverters,
    primaryInverterAutoQuantity,
    hasAutoCalculatedInverterQuantity,
    designData: {
      ...(state.designData || {}),
      inverter,
      inverterQuantity,
      inverter_quantity: inverterQuantity,
      primaryInverterAutoQuantity,
      hasAutoCalculatedInverterQuantity,
      additional_inverters: persistedAdditionalInverters
    }
  };
}

export function buildReportInverterAutoModePatch(auto) {
  return {
    hasAutoCalculatedInverterQuantity: !auto,
    primaryInverterAutoQuantity: Boolean(auto)
  };
}

export function toggleReportPanelSelection(state = {}, roofIndex, panelIndex) {
  const selectedPanels = { ...(state.selectedPanels || {}) };
  const segmentId = state.design?.[roofIndex]?.id;
  if (!segmentId || !Array.isArray(selectedPanels?.[segmentId])) return null;
  if (panelIndex < 0 || panelIndex >= selectedPanels[segmentId].length) {
    return null;
  }

  selectedPanels[segmentId] = [...selectedPanels[segmentId]];
  selectedPanels[segmentId][panelIndex] = selectedPanels[segmentId][panelIndex]
    ? 0
    : 1;
  return { selectedPanels, productionIsCalculating: true };
}

export function setAllReportPanelsSelection(state = {}, selected) {
  const selectedPanels = {};
  Object.entries(state.selectedPanels || {}).forEach(([key, values]) => {
    selectedPanels[key] = Array(Array.isArray(values) ? values.length : 0).fill(
      selected ? 1 : 0
    );
  });
  if (selected) return { selectedPanels, productionIsCalculating: true };
  return {
    selectedPanels,
    totalPanels: 0,
    systemSize: 0,
    solarProduction: 0,
    productionPerPanelData: [],
    productionPerStringData: [],
    offset: 0
  };
}

export function buildReportProductionCallback({
  state = {},
  annualUsage,
  panelData,
  overrides = {}
} = {}) {
  const design = state.design || [];
  const designData = state.designData || {};
  const { totalCount, perRoofCounts } = getPanelCounts({
    panelData,
    design,
    designPanels: designData?.panels
  });
  const selectedPanelsPerRoof =
    overrides.selectedPanelsPerRoof ??
    getSelectedPanelsPerRoofFromDesign(design, state.selectedPanels);
  const persistedProductionPerPanel =
    designData?.productionPerPanel ?? designData?.production_per_panel ?? [];
  const persistedProductionPerString =
    designData?.productionPerString ?? designData?.production_per_string ?? [];
  const valueOr = (key, fallback) =>
    overrides[key] !== undefined ? overrides[key] : fallback;

  return buildProductionCallbackPayload({
    annualUsage,
    annualKwh: valueOr('annualKwh', state.solarProduction ?? 0),
    monthlyKwh: valueOr(
      'monthlyKwh',
      state.monthlyProduction ?? Array(12).fill(0)
    ),
    systemSizeKw: valueOr('systemSizeKw', state.systemSize ?? 0),
    numTotalPanels: valueOr('numTotalPanels', totalCount),
    selectedPanelCount: valueOr('selectedPanelCount', state.totalPanels ?? 0),
    perRoofCounts: valueOr('perRoofCounts', perRoofCounts),
    segments: design,
    selectedPanelsPerRoof,
    productionPerPanel: valueOr(
      'productionPerPanel',
      state.productionPerPanelData ?? persistedProductionPerPanel
    ),
    productionPerString: valueOr(
      'productionPerString',
      state.productionPerStringData ?? persistedProductionPerString
    ),
    strings: valueOr('strings', state.stringingData ?? null),
    invertersUsed: valueOr('invertersUsed', state.invertersUsed ?? null),
    monthlyClippingKwh: valueOr(
      'monthlyClippingKwh',
      state.monthlyClippingKwh ?? Array(12).fill(0)
    ),
    clippingKwh: valueOr('clippingKwh', state.clippingKwh ?? 0),
    dcAcRatio: valueOr('dcAcRatio', state.dcAcRatio ?? null),
    panelCount: valueOr(
      'panelCount',
      valueOr('selectedPanelCount', state.totalPanels ?? 0)
    ),
    mpptCountTotal: valueOr('mpptCountTotal', state.mpptCountTotal ?? null),
    perRoofAnnualKwh: overrides.perRoofAnnualKwh,
    perRoofMonthlyAc: overrides.perRoofMonthlyAc,
    perRoofMonthlyPoa: overrides.perRoofMonthlyPoa,
    perRoofClippingKwh: overrides.perRoofClippingKwh,
    limitReached:
      overrides.limitReached ??
      Boolean(designData?.limitReached ?? designData?.limit_reached)
  });
}

function findRoofPanelsData(designData, idx, designId) {
  const list = designData?.panels;
  if (!list) return null;

  const designMap = designData?.design_map || designData?.designMap;
  if (designMap && typeof designMap === 'object' && designId in designMap) {
    const mappedPanelGroupId = designMap[designId];
    if (Array.isArray(list)) {
      const mapped = list.find(
        (el) => String(el?.id) === String(mappedPanelGroupId)
      );
      if (mapped) return mapped;
    } else if (list?.[mappedPanelGroupId]) {
      return list[mappedPanelGroupId];
    }
  }

  const suffixMatch = String(designId ?? '').match(/-(\d+)$/);
  if (Array.isArray(list) && suffixMatch) {
    const persistedIndex = Number(suffixMatch[1]);
    if (list[persistedIndex]) return list[persistedIndex];
  }

  return Array.isArray(list) ? list[idx] || null : null;
}

function panelOrientationCount(value) {
  if (Array.isArray(value?.points)) return value.points.length;
  return Number(value) || 0;
}

function panelIndexFrom2D(roofPanelsData, pi) {
  const hrCount = panelOrientationCount(roofPanelsData?.horizontal);
  const vcCount = panelOrientationCount(roofPanelsData?.vertical);
  if (pi >= vcCount) return pi - vcCount;
  return pi + hrCount;
}

function panelIndexFrom3D(roofPanelsData, panelIndex) {
  const hrCount = panelOrientationCount(roofPanelsData?.horizontal);
  const vcCount = panelOrientationCount(roofPanelsData?.vertical);
  const pi = Number(panelIndex);
  if (Number.isNaN(pi)) return -1;
  if (pi >= hrCount) return pi - hrCount;
  return pi + vcCount;
}

export function syncReportSelectedPanelFrom2D(state = {}) {
  const { selectedPanels, selectedObjects, designData, design } = state;
  const panels = {};
  const selectedPanelsSafe =
    selectedPanels && typeof selectedPanels === 'object' ? selectedPanels : {};
  const designArr = Array.isArray(design) ? design : [];

  designArr.forEach((roof, idx) => {
    const designId = roof?.id;
    if (!designId) return;

    const pnls = Array.isArray(selectedPanelsSafe[designId])
      ? selectedPanelsSafe[designId]
      : [];
    const roofPanelsData =
      findRoofPanelsData(designData, idx, designId) || roof;

    const indices = pnls
      .map((v, i) => (v ? panelIndexFrom2D(roofPanelsData, i) : -1))
      .filter((v) => v !== -1);

    panels[idx] = indices;
  });

  return {
    selectedObjects: {
      ...(selectedObjects || createEmptySelectedObjects()),
      panels
    }
  };
}

export function syncReportSelectedPanelFrom3D(state = {}) {
  const {
    selectedPanels: selected,
    selectedObjects,
    designData,
    design
  } = state;

  const selectedPanels = {};
  const selectedSafe = selected && typeof selected === 'object' ? selected : {};
  const designArr = Array.isArray(design) ? design : [];

  designArr.forEach(({ id, vertical = 0, horizontal = 0 }) => {
    if (!id) return;
    const total = Number(vertical || 0) + Number(horizontal || 0);
    const existing = selectedSafe[id];
    selectedPanels[id] = Array(
      Array.isArray(existing) ? existing.length : total
    ).fill(0);
  });

  Object.keys(selectedObjects?.panels || {}).forEach((ri) => {
    const roofIdx = Number(ri);
    const designId = designArr?.[roofIdx]?.id;
    if (!designId || !Array.isArray(selectedPanels[designId])) return;

    const roofPanelsData =
      findRoofPanelsData(designData, roofIdx, designId) ||
      designArr[roofIdx];

    const pnls = selectedObjects?.panels?.[ri];
    if (!Array.isArray(pnls)) return;

    const indices = pnls.map((v) => panelIndexFrom3D(roofPanelsData, v));
    indices.forEach((pi) => {
      if (pi < 0 || pi >= selectedPanels[designId].length) return;
      selectedPanels[designId][pi] = 1;
    });
  });

  Object.entries(selectedSafe).forEach(([id, value]) => {
    if (selectedPanels[id] || !Array.isArray(value)) return;
    selectedPanels[id] = Array(value.length).fill(0);
  });

  return { selectedPanels };
}

export function clearReportPanelSelection(state = {}, forceClear = false) {
  const selectedObjects = state.selectedObjects || createEmptySelectedObjects();
  const panels =
    forceClear || Object.keys(selectedObjects?.panels || {}).length > 0
      ? {}
      : selectedObjects?.panels || {};
  return {
    selectedObjects: { ...selectedObjects, panels }
  };
}
